/**
 * Cart free gift progress bar
 *
 * Custom elements:
 *   <gift-bar>      cart progress bar (snippets/gift-bar.liquid)
 *   <gift-callout>  "free over X" box on the gift's product page (snippets/product-gift-callout.liquid)
 *
 * Both only show between their start and end dates and count down to the end.
 * Re-reads the cart after every theme cart update. Once the spend reaches the minimum
 * the gift is added on its own; below it the gift is taken out again. The gift's own
 * line never counts towards the minimum. A shopper who removes the gift themselves
 * keeps it out for the rest of the visit and gets the "add your gift" button instead.
 */
if (!customElements.get('gift-bar')) {
  // One cart fetch serves every bar on the page (cart page and drawer can both have one)
  let pending = null;
  let removing = false;
  let adding = false;

  // Remembered per visit: whether we put the gift in, and whether the shopper took it out
  const flag = {
    get(name) {
      try {
        return sessionStorage.getItem(`gift_bar:${name}`) === '1';
      } catch (error) {
        return false;
      }
    },
    set(name, value) {
      try {
        if (value) sessionStorage.setItem(`gift_bar:${name}`, '1');
        else sessionStorage.removeItem(`gift_bar:${name}`);
      } catch (error) {}
    },
  };

  const formatMoney = (cents) => {
    const format = (window.theme && window.theme.moneyFormat) || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Whole amounts drop their decimals, like money_without_trailing_zeros does
    const fraction = decimals === '00' ? '' : decimals;
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withDots)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, fraction ? `${withDots},${fraction}` : withDots)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount\s*\}\}/, fraction ? `${withCommas}.${fraction}` : withCommas);
  };

  // Any theme that isn't live (theme dev, preview links, drafts) shows the campaign regardless
  // of its dates. ?campaign_preview turns that on anywhere and ?campaign_preview=off turns it
  // off, so the real schedule can be checked on a preview too. The choice lasts for the rest
  // of the tab's session (the announcement bar reads the same flag).
  const isPreview = () => {
    const match = location.search.match(/[?&]campaign_preview(?:=([^&]*))?/);
    let choice = match ? (match[1] === 'off' ? 'off' : '1') : null;
    try {
      if (choice) sessionStorage.setItem('campaign_preview', choice);
      choice = sessionStorage.getItem('campaign_preview');
    } catch (error) {}

    if (choice === 'off') return false;
    if (choice === '1') return true;
    const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    return Boolean(role && role !== 'main');
  };

  const parseDate = (raw) => (raw ? Date.parse(raw.trim().replace(' ', 'T') + (raw.split(':').length < 3 ? ':00' : '')) : NaN);

  const isRunning = (element) => {
    if (isPreview() || (window.Shopify && window.Shopify.designMode)) return true;
    const start = parseDate(element.dataset.start);
    const end = parseDate(element.dataset.end);
    const now = Date.now();
    return !((!isNaN(start) && now < start) || (!isNaN(end) && now >= end));
  };

  const pad = (number) => String(number).padStart(2, '0');

  // Fills [data-gift-countdown] with "3 gün 04:12:09" until the end date, then hides [data-gift-timer]
  const startCountdown = (element) => {
    const timer = element.querySelector('[data-gift-timer]');
    const output = element.querySelector('[data-gift-countdown]');
    const end = parseDate(element.dataset.end);
    if (!timer || !output || isNaN(end)) return null;

    const dayLabel = output.dataset.dayLabel || 'gün';
    const tick = () => {
      const seconds = Math.floor((end - Date.now()) / 1000);
      if (seconds <= 0) {
        timer.hidden = true;
        clearInterval(interval);
        return;
      }
      const days = Math.floor(seconds / 86400);
      const clock = `${pad(Math.floor((seconds % 86400) / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
      output.textContent = days > 0 ? `${days} ${dayLabel} ${clock}` : clock;
      timer.hidden = false;
    };
    const interval = setInterval(tick, 1000);
    tick();
    return interval;
  };

  class GiftCallout extends HTMLElement {
    connectedCallback() {
      if (!isRunning(this)) return;
      this.hidden = false;
      this.interval = startCountdown(this);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }
  }

  customElements.define('gift-callout', GiftCallout);

  class GiftBar extends HTMLElement {
    connectedCallback() {
      this.limit = parseInt(this.dataset.limit, 10);
      this.variantId = this.dataset.variantId;

      if (!isRunning(this)) return;

      this.active = true;
      this.interval = startCountdown(this);
      // Only a cart change adds the gift, so opening a page never pops the cart drawer open
      this.onCartChange = () => GiftBar.refresh(true);
      document.addEventListener('theme:cart:added', this.onCartChange);
      GiftBar.refresh(false);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:added', this.onCartChange);
      clearInterval(this.interval);
    }

    static refresh(canAdd) {
      if (pending) return pending.then(() => GiftBar.refresh(canAdd));
      pending = fetch(`${window.theme.routes.cart_url}.js`, {headers: {Accept: 'application/json'}})
        .then((response) => response.json())
        .then((cart) => document.querySelectorAll('gift-bar').forEach((bar) => bar.active && bar.render(cart, canAdd)))
        .catch((error) => console.log(error))
        .finally(() => {
          pending = null;
        });
      return pending;
    }

    render(cart, canAdd) {
      const giftLines = cart.items.filter((item) => String(item.variant_id) === this.variantId);
      const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifying = cart.total_price - giftTotal;
      const unlocked = qualifying >= this.limit;

      if (!unlocked && giftLines.length) {
        this.removeGift();
      } else if (unlocked && giftLines.length) {
        flag.set('declined', false);
      } else if (unlocked && !adding) {
        // We added it earlier and it's gone now, so the shopper removed it on purpose
        if (flag.get('added')) {
          flag.set('added', false);
          flag.set('declined', true);
        } else if (canAdd && !flag.get('declined')) {
          this.addGift();
        }
      }

      let state = 'progress';
      if (unlocked) state = giftLines.length ? 'added' : 'unlocked';

      this.querySelectorAll('[data-gift-state]').forEach((element) => {
        element.hidden = element.dataset.giftState !== state;
      });

      const left = this.querySelector('[data-gift-left]');
      if (left) left.textContent = formatMoney(Math.max(this.limit - qualifying, 0));

      const fill = this.querySelector('[data-gift-progress]');
      if (fill) fill.style.width = `${Math.min(Math.max((qualifying / this.limit) * 100, 0), 100)}%`;

      this.classList.toggle('is-unlocked', unlocked);

      this.hidden = cart.item_count === 0;
    }

    // Clicks the bar's own add button, so the theme adds the gift and redraws the cart as usual
    addGift() {
      const button = this.querySelector('[data-gift-state="unlocked"] [data-add-to-cart]');
      if (!button) return;
      adding = true;
      flag.set('added', true);
      document.addEventListener('theme:cart:added', () => (adding = false), {once: true});
      button.click();
    }

    // Uses the line's own remove link so the theme redraws the cart as usual
    removeGift() {
      if (removing) return;
      const line = document.querySelector(`[data-cart-item][data-item^="${this.variantId}:"]`);
      const remove = line && line.querySelector('[data-item-remove]');
      if (!remove) return;
      removing = true;
      flag.set('added', false);
      document.addEventListener('theme:cart:added', () => (removing = false), {once: true});
      remove.click();
    }
  }

  customElements.define('gift-bar', GiftBar);
}
